import Centered from 'components/centered'
import { Spinner } from 'components/spinner'
import IRClient from '@/modules/ir/apiClient'
import type { FindexAsset } from '@/types/models/asset'
import { useMemoAsync } from '@/utils/async/useMemoAsync'
import { defineVariants } from '@/utils/style/variant'
import { type FindexInvestment, type FullInvestment } from 'findef'
import { computed, defineComponent, unref, type MaybeRef } from 'vue'
import { InvestmentTree } from '../investment-tree'
import { getInvestmentId } from '@/utils/investment'
import { useForexTickers } from '@/integrations/findex/hooks/forex/useForexTickers'

const useVariants = defineVariants({
  slots: {
    base: {
      class: 'border border-gray-300 px-6 py-4 space-y-4 w-full flex flex-col min-h-[256px]'
    },
    treeContainer: {
      class: 'w-full'
    }
  },
  variants: {
    scrollable: {
      true: {
        base: {},
        treeContainer: {
          class: 'pretty-scrollbar pl-4 flex-1',
          style: {
            maxWidth: '100%',
            maxHeight: '500px',
            overflow: 'auto'
          }
        }
      }
    }
  }
})

const OwnershipTree = defineComponent<{
  asset?: FindexAsset
  investment?: MaybeRef<FindexInvestment | FullInvestment>;
  name?: string
  parentId?: string
  childId?: string
  selectedId?: string
  scrollable?: boolean
  isPreview?: boolean
  exclusive?: boolean
  mockInvestment?: MaybeRef<FindexInvestment | FullInvestment>;
  realParentId?: MaybeRef<string>;
}>({
  setup(props) {
    const forex = useForexTickers();
    const assetsQuery = useMemoAsync(async () => {
      console.log('assetsQuery()')
      const investments = (await IRClient.getInvestments()).filter((it) => !!it.asset)
      return investments.map((it) => it.asset)
    })

    const assetCount = computed(() => assetsQuery?.data?.length || 0);

    const scrollable = computed(() => props.scrollable || false)

    const variants = useVariants({ scrollable });

    const expandedId = computed(() => {
      const inv = unref(props.investment);
      return inv ? getInvestmentId(inv as unknown as any) : props.selectedId
    });

    return () => {
      if (assetsQuery.loading)
        return (
          <Centered>
            <Spinner />
          </Centered>
        )
      return (
        <div class={variants.value.base.class} style={variants.value.base.style}>
          <div class="pb-3 border-b border-gray-200 flex-none">
            <div>
              <h3 class="text-gray-900 text-sm font-semibold">Ownership Structure</h3>
              <p class="text-gray-500 text-xs font-normal">
                {assetCount.value} Assets in Total
              </p>
            </div>
          </div>
          <div
            class={variants.value.treeContainer.class}
            style={variants.value.treeContainer.style}
          >
            <InvestmentTree realParentId={props.realParentId} forex={forex} mockInvestment={props.mockInvestment} investment={props.investment} treeProps={{
              expandedId: expandedId.value
            }} />
          </div>
        </div>
      )
    }
  }
})

OwnershipTree.props = [
  'asset',
  'parentId',
  'realParentId',
  'childId',
  'name',
  'selectedId',
  'scrollable',
  'isPreview',
  'exclusive',
  'investment',
  'mockInvestment'
]

export default OwnershipTree
